import React, { useMemo, useState } from "react";
import axios from "../../utils/axiosInstance";
import { useNavigate } from "react-router-dom";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import "flag-icon-css/css/flag-icons.min.css";
import { parsePhoneNumberFromString } from "libphonenumber-js";

export default function Register() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [phoneDigits, setPhoneDigits] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const navigate = useNavigate();

  const isValidEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(v || "").trim());

  const phoneE164 = useMemo(() => {
    if (!phoneDigits) return "";
    const raw = phoneDigits.startsWith("+") ? phoneDigits : `+${phoneDigits}`;
    const parsed = parsePhoneNumberFromString(raw);
    return parsed?.isValid() ? parsed.number : "";
  }, [phoneDigits]);

  const canSubmit = useMemo(() => {
    return isValidEmail(email) && Boolean(phoneE164) && String(password || "").trim().length >= 6;
  }, [email, phoneE164, password]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    const cleanEmail = String(email || "").trim().toLowerCase();
    const cleanPassword = String(password || "").trim();

    if (!isValidEmail(cleanEmail)) {
      setError("Veuillez entrer une adresse email valide.");
      return;
    }

    if (!phoneE164) {
      setError("Veuillez entrer un numéro de téléphone valide (avec indicatif pays).");
      return;
    }

    if (cleanPassword.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }

    try {
      const response = await axios.post(`/auth/register`, {
        email: cleanEmail,
        phoneNumber: phoneE164, // ✅ store +E.164
        password: cleanPassword,
      });

      setSuccess(response.data.message || "Registration successful!");
      setTimeout(() => navigate("/login"), 2500);
    } catch (err) {
      console.error("❌ Registration error:", err);
      setError(err.response?.data?.message || "Error registering. Please try again.");
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex items-center justify-center px-4">
        <div className="w-full max-w-md rounded-2xl border border-green-200 dark:border-green-500/30 bg-green-50 dark:bg-green-500/10 p-6 text-center">
          <h2 className="text-xl font-semibold text-green-800 dark:text-green-200">Success!</h2>
          <p className="mt-2 text-green-700 dark:text-green-200">{success}</p>
          <p className="mt-4 text-sm text-slate-600 dark:text-slate-300">
            Redirecting to login page...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <div className="mb-6 text-center">
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900 dark:text-white">
            Create your account
          </h1>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
            Start sending money confidently
          </p>
        </div>

        <form
          onSubmit={handleSubmit}
          className="rounded-2xl border border-black/5 dark:border-white/10 bg-white dark:bg-slate-900 shadow-sm p-6"
        >
          {error && (
            <div className="mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-200">
              {error}
            </div>
          )}

          <div className="space-y-3">
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Email"
              required
            />

            <div className="rounded-xl border border-black/10 dark:border-white/10 bg-white dark:bg-slate-950 px-2 py-2">
              <PhoneInput
                country={"cm"}
                value={phoneDigits}
                onChange={(value) => setPhoneDigits(value)}
                enableSearch
                inputStyle={{
                  width: "100%",
                  border: "none",
                  height: "42px",
                  background: "transparent",
                }}
                buttonStyle={{
                  border: "none",
                  background: "transparent",
                }}
                containerStyle={{ width: "100%" }}
                inputProps={{ name: "phone", required: true }}
              />
              <div className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                {phoneE164 ? `✅ ${phoneE164}` : "Enter a valid number (with country code)."}
              </div>
            </div>

            <Input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Password"
              required
              minLength={6}
            />

            <button
              type="submit"
              disabled={!canSubmit}
              className="w-full rounded-xl bg-slate-900 text-white py-3 font-medium hover:opacity-95 transition
                         disabled:opacity-50 disabled:cursor-not-allowed
                         dark:bg-white dark:text-slate-900"
            >
              Create account
            </button>

            <p className="text-center text-sm text-slate-600 dark:text-slate-300">
              Already have an account?{" "}
              <button
                type="button"
                onClick={() => navigate("/login")}
                className="font-medium text-blue-600 dark:text-blue-400 hover:underline"
              >
                Sign in
              </button>
            </p>
          </div>
        </form>
      </div>
    </div>
  );
}

function Input(props) {
  return (
    <input
      {...props}
      className="w-full rounded-xl border border-black/10 dark:border-white/10
                 bg-white dark:bg-slate-950 px-4 py-3 text-sm
                 text-slate-900 dark:text-white placeholder:text-slate-400
                 focus:outline-none focus:ring-2 focus:ring-slate-900/20 dark:focus:ring-white/20"
    />
  );
}
